import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Inbox, Phone, Mail, MapPin, CalendarDays } from 'lucide-react'
import { toast } from 'sonner'
import { useLeads, type Lead, type LeadStatus } from '@/hooks/use-leads'
import {
  Page, PageHeader, Card, Table, Trow, SkeletonRows, EmptyState,
  Pagination, TabPills, StatusPill, btnSecondary, btnPrimary,
} from '@/components/rm'
import { Modal } from '@/components/rm/form'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/_app/leads')({ component: LeadsPage })

const COLS = '1.3fr 1fr 110px 130px 110px'
const STATUSES: LeadStatus[] = ['new', 'contacted', 'quoted', 'won', 'lost']
const SOURCE_LABEL: Record<string, string> = {
  booking: 'Online booking', web_form: 'Website form', email: 'Email', manual: 'Added manually',
}

const fmtWhen = (iso: string) =>
  phFormat(new Date(iso), 'datetime', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })

function LeadsPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<LeadStatus | ''>('')
  const [page, setPage] = useState(0)
  const [open, setOpen] = useState<Lead | null>(null)

  const { leads, total, pageSize, isLoading, isError, error, refetch, setStatus: mutate } = useLeads({ status, page })

  const move = async (l: Lead, s: LeadStatus) => {
    try {
      await mutate.mutateAsync({ id: l.id, status: s })
      setOpen(null)
      toast.success(`Marked ${s}`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not update')
    }
  }

  return (
    <Page>
      <PageHeader title="Leads" subtitle={`${total} enquir${total === 1 ? 'y' : 'ies'}`} />

      <div className="mt-5">
        <TabPills
          chips={[{ id: '', label: 'All', count: total }, ...STATUSES.map((s) => ({ id: s, label: s[0].toUpperCase() + s.slice(1) }))]}
          active={status}
          onChange={(id) => { setStatus(id as LeadStatus | ''); setPage(0) }}
        />
      </div>

      <div className="mt-5">
        <Card>
          {isLoading ? (
            <SkeletonRows count={5} />
          ) : isError ? (
            // Distinct from "no leads" — an empty inbox shown because the fetch failed would make a
            // business think nobody enquired today.
            <div className="px-5 py-10 text-center">
              <p className="text-[14px] font-semibold text-token-primary">Couldn’t load your leads</p>
              <p className="mt-1 text-[13px] text-token-secondary">{error?.message ?? 'Please try again.'}</p>
              <button className={btnSecondary + ' mt-4'} onClick={() => refetch()}>Try again</button>
            </div>
          ) : leads.length === 0 ? (
            <EmptyState
              label={status ? `No ${status} leads.` : 'No enquiries yet. Turn on online booking in Settings to start taking them.'}
              cta={status ? undefined : 'Booking settings'}
              onCta={status ? undefined : () => navigate({ to: '/settings' })}
            />
          ) : (
            <Table
              cols={COLS}
              header={<><span>Name</span><span>Contact</span><span>Source</span><span>Received</span><span className="text-right">Status</span></>}
            >
              {leads.map((l, idx) => (
                <Trow key={l.id} cols={COLS} last={idx === leads.length - 1} onClick={() => setOpen(l)}>
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 truncate font-bold text-token-primary">
                      <Inbox className="h-3.5 w-3.5 flex-none text-token-secondary" />
                      {l.name || 'Unnamed enquiry'}
                    </span>
                    {l.message && <span className="block truncate text-[12px] font-normal text-token-secondary">{l.message}</span>}
                  </span>
                  <span className="truncate text-[13px] text-token-secondary">{l.phone || l.email || '—'}</span>
                  <span className="truncate text-[12.5px] text-token-secondary">{SOURCE_LABEL[l.source] ?? l.source}</span>
                  <span className="text-[12.5px] tabular-nums text-token-secondary">{fmtWhen(l.created_at)}</span>
                  <span className="text-right"><StatusPill status={l.status} /></span>
                </Trow>
              ))}
              <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} />
            </Table>
          )}
        </Card>
      </div>

      <Modal
        open={!!open}
        onClose={() => setOpen(null)}
        title={open?.name || 'Enquiry'}
        footer={<button className={btnSecondary} onClick={() => setOpen(null)}>Close</button>}
      >
        {open && (
          <div className="space-y-4">
            <div className="space-y-1.5 text-[13.5px]">
              {open.phone && <p className="flex items-center gap-2 text-token-primary"><Phone className="h-4 w-4 text-token-secondary" /><a href={`tel:${open.phone.replace(/[^\d+]/g, '')}`} className="font-semibold">{open.phone}</a></p>}
              {open.email && <p className="flex items-center gap-2 text-token-primary"><Mail className="h-4 w-4 text-token-secondary" /><a href={`mailto:${open.email}`} className="font-semibold">{open.email}</a></p>}
              {open.address && <p className="flex items-center gap-2 text-token-primary"><MapPin className="h-4 w-4 text-token-secondary" />{open.address}</p>}
              <p className="flex items-center gap-2 text-token-secondary"><CalendarDays className="h-4 w-4" />{SOURCE_LABEL[open.source] ?? open.source} · {fmtWhen(open.created_at)}</p>
            </div>

            {open.message && (
              <p className="rounded-[10px] border border-token-subtle bg-token-surface p-3 text-[13.5px] text-token-primary">{open.message}</p>
            )}

            {open.task_id && (
              <button className={btnPrimary + ' w-full'} onClick={() => navigate({ to: '/jobs/$id', params: { id: String(open.task_id) } })}>
                Open the booked job
              </button>
            )}

            <div>
              <p className="mb-2 text-[11px] font-black uppercase tracking-[0.12em] text-token-secondary">Move to</p>
              <div className="flex flex-wrap gap-2">
                {STATUSES.filter((s) => s !== open.status).map((s) => (
                  <button key={s} className={btnSecondary} onClick={() => move(open, s)} disabled={mutate.isPending}>
                    {s[0].toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </Page>
  )
}
